import { useState } from 'react';
import { Type, Copy, Check, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToolUsage } from '@/hooks/useToolUsage';
import { toast } from 'sonner';

const LOREM_WORDS = [
  'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
  'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
  'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
  'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
  'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
  'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
  'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
  'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum', 'perspiciatis', 'unde',
  'omnis', 'iste', 'natus', 'error', 'voluptatem', 'accusantium', 'doloremque',
  'laudantium', 'totam', 'rem', 'aperiam', 'eaque', 'ipsa', 'quae', 'ab', 'illo',
  'inventore', 'veritatis', 'quasi', 'architecto', 'beatae', 'vitae', 'dicta',
];

type OutputType = 'paragraphs' | 'sentences' | 'words';

const LoremIpsumGenerator = () => {
  useToolUsage('lorem_ipsum'); // Track tool usage

  const [type, setType] = useState<OutputType>('paragraphs');
  const [quantity, setQuantity] = useState(3);
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const generateWord = () => {
    return LOREM_WORDS[Math.floor(Math.random() * LOREM_WORDS.length)];
  };

  const generateSentence = (wordCount: number = 8 + Math.floor(Math.random() * 12)) => {
    const words = [];
    for (let i = 0; i < wordCount; i++) {
      words.push(generateWord());
    }
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
    return words.join(' ') + '.';
  };

  const generateParagraph = (sentenceCount: number = 4 + Math.floor(Math.random() * 4)) => {
    const sentences = [];
    for (let i = 0; i < sentenceCount; i++) {
      sentences.push(generateSentence());
    }
    return sentences.join(' ');
  };

  const generate = () => {
    let result = '';

    switch (type) {
      case 'words':
        const words = [];
        for (let i = 0; i < quantity; i++) {
          words.push(generateWord());
        }
        words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
        result = words.join(' ');
        break;

      case 'sentences':
        const sentences = [];
        for (let i = 0; i < quantity; i++) {
          sentences.push(generateSentence());
        }
        result = sentences.join(' ');
        break;

      case 'paragraphs':
        const paragraphs = [];
        for (let i = 0; i < quantity; i++) {
          paragraphs.push(generateParagraph());
        }
        result = paragraphs.join('\n\n');
        break;
    }

    // Always start with "Lorem ipsum dolor sit amet"
    if (result.length > 0) {
      const prefix = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. ';
      result = prefix + result.slice(result.indexOf(' ') + 1);
    }

    setOutput(result);
    toast.success('Lorem ipsum generated!');
  };

  const copyOutput = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Type className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Lorem Ipsum Generator</h1>
          <p className="text-muted-foreground">
            Generate placeholder text for your designs
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Controls */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle>Options</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Generate</Label>
              <Select value={type} onValueChange={(v) => setType(v as OutputType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paragraphs">Paragraphs</SelectItem>
                  <SelectItem value="sentences">Sentences</SelectItem>
                  <SelectItem value="words">Words</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              />
            </div>

            <Button onClick={generate} className="w-full gap-2">
              <RefreshCw className="h-4 w-4" />
              Generate
            </Button>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass border-border/50 lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Generated Text</span>
              {output && (
                <Button variant="ghost" size="sm" onClick={copyOutput} className="gap-2">
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  Copy
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Click 'Generate' to create lorem ipsum text..."
              value={output}
              readOnly
              className="min-h-[300px] resize-none bg-muted/30"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoremIpsumGenerator;